import React, { useCallback, useEffect, useState } from "react";
import { NavLink, Outlet, useNavigate } from "react-router-dom";
import {
    Home, Briefcase, Camera, User, Clock as ClockIcon, LogOut, RefreshCw, WifiOff, Loader2,
    X, Trash2, Image as ImageIcon, Gauge, Fuel, Truck, PlayCircle, StopCircle,
    AlertTriangle, BookOpen,
} from "lucide-react";
import { useAuth } from "../contexts/AuthContext";
import { BrandLockup } from "../components/Brand";
import { usePendingCount } from "../hooks/usePendingCount";
import {
    drainQueue, broadcastPending, listQueued, removeQueued,
} from "../lib/offlineQueue";
import dict from "../i18n/es-MX";

const TABS = [
    { key: "home", path: "/campo", icon: Home, end: true },
    { key: "myJobs", path: "/campo/trabajos", icon: Briefcase },
    { key: "camera", path: "/campo/camara", icon: Camera },
    { key: "hours", path: "/campo/horas", icon: ClockIcon },
    { key: "profile", path: "/campo/perfil", icon: User },
];

const KIND_META = {
    photo_upload:  { Icon: ImageIcon,    label: "Foto" },
    job_photo:     { Icon: ImageIcon,    label: "Foto de trabajo" },
    meter_reading: { Icon: Gauge,        label: "Lectura medidor" },
    fuel_log:      { Icon: Fuel,         label: "Combustible" },
    viaje:         { Icon: Truck,        label: "Viaje" },
    idle_toggle:   { Icon: AlertTriangle,label: "En espera" },
    checkin:       { Icon: PlayCircle,   label: "Inicio de jornada" },
    checkout:      { Icon: StopCircle,   label: "Fin de jornada" },
};

import { fmtDateTime as fmtRelDate } from "../lib/dateFormat";

function PendingDetailModal({ open, onClose, online, onDrain, syncing }) {
    const [items, setItems] = useState([]);
    const refresh = useCallback(async () => {
        const rows = await listQueued();
        rows.sort((a, b) => (a.queuedAt < b.queuedAt ? -1 : 1));
        setItems(rows);
    }, []);
    useEffect(() => { if (open) refresh(); }, [open, refresh]);

    const removeOne = async (id) => {
        await removeQueued(id);
        await broadcastPending();
        refresh();
    };
    const retry = async () => {
        await onDrain();
        refresh();
    };

    if (!open) return null;
    return (
        <div
            className="fixed inset-0 z-[90] bg-black/70 backdrop-blur-sm flex items-end sm:items-center justify-center"
            onClick={onClose}
            data-testid="pending-modal"
        >
            <div
                className="w-full sm:max-w-md bg-slate-900 border border-slate-800 rounded-t-2xl sm:rounded-2xl max-h-[80vh] flex flex-col"
                onClick={(e) => e.stopPropagation()}
            >
                <header className="px-4 py-3 border-b border-slate-800 flex items-center justify-between">
                    <div>
                        <h2 className="font-display text-base font-bold tracking-tight">{dict.pending.label}</h2>
                        <p className="text-xs text-slate-400">{items.length === 0 ? dict.pending.none : `${items.length} ${items.length === 1 ? "pendiente" : "pendientes"}`}</p>
                    </div>
                    <button type="button" onClick={onClose} data-testid="pending-modal-close" className="w-9 h-9 rounded-md hover:bg-slate-800 text-slate-400 hover:text-slate-100 inline-flex items-center justify-center">
                        <X size={18}/>
                    </button>
                </header>

                <div className="flex-1 overflow-y-auto px-2 py-2 space-y-1">
                    {items.length === 0 && (
                        <div className="text-center text-sm text-slate-500 py-8">{dict.pending.none}</div>
                    )}
                    {items.map((it) => {
                        const meta = KIND_META[it.kind] || { Icon: AlertTriangle, label: it.kind };
                        const Icon = meta.Icon;
                        return (
                            <div key={it.id} className="px-3 py-2.5 rounded-lg hover:bg-slate-800/60 flex items-center gap-3" data-testid={`pending-item-${it.id}`}>
                                <div className="w-9 h-9 rounded-md bg-brand-orange/15 text-brand-orange flex items-center justify-center shrink-0">
                                    <Icon size={16}/>
                                </div>
                                <div className="flex-1 min-w-0">
                                    <div className="text-sm font-medium text-slate-100 truncate">{meta.label}</div>
                                    <div className="text-[11px] text-slate-500">{fmtRelDate(it.queuedAt)}</div>
                                </div>
                                <button
                                    type="button"
                                    onClick={() => removeOne(it.id)}
                                    data-testid={`pending-item-delete-${it.id}`}
                                    className="w-8 h-8 rounded-md hover:bg-red-500/15 text-slate-500 hover:text-red-300 inline-flex items-center justify-center"
                                    title="Eliminar"
                                >
                                    <Trash2 size={14}/>
                                </button>
                            </div>
                        );
                    })}
                </div>

                <footer className="px-4 py-3 border-t border-slate-800 flex items-center gap-2">
                    <button
                        type="button"
                        onClick={retry}
                        disabled={items.length === 0 || !online || syncing}
                        data-testid="pending-retry-all"
                        className="flex-1 h-10 rounded-md bg-brand-orange hover:bg-orange-400 text-slate-950 text-sm font-semibold uppercase tracking-wider inline-flex items-center justify-center gap-2 disabled:opacity-40 disabled:cursor-not-allowed transition"
                    >
                        {syncing
                            ? <Loader2 className="animate-spin" size={14}/>
                            : <RefreshCw size={14}/>}
                        {dict.pending.syncNow}
                    </button>
                </footer>
            </div>
        </div>
    );
}

export default function MobileShell() {
    const { logout } = useAuth();
    const navigate = useNavigate();
    const pending = usePendingCount();
    const [online, setOnline] = useState(typeof navigator !== "undefined" ? navigator.onLine : true);
    const [syncing, setSyncing] = useState(false);
    const [flash, setFlash] = useState("");
    const [modalOpen, setModalOpen] = useState(false);

    const sync = useCallback(async () => {
        if (syncing) return { synced: 0, dropped: 0, total: 0 };
        setSyncing(true);
        try {
            const r = await drainQueue();
            if (r.synced > 0 && r.dropped === 0) setFlash(dict.pending.synced);
            else if (r.dropped > 0) setFlash(dict.pending.partial);
            return r;
        } finally {
            setSyncing(false);
            await broadcastPending();
            setTimeout(() => setFlash(""), 2200);
        }
    }, [syncing]);

    useEffect(() => {
        const on = () => { setOnline(true); sync(); };
        const off = () => setOnline(false);
        window.addEventListener("online", on);
        window.addEventListener("offline", off);
        broadcastPending();
        return () => {
            window.removeEventListener("online", on);
            window.removeEventListener("offline", off);
        };
    }, [sync]);

    const handleLogout = () => {
        logout();
        navigate("/login", { replace: true });
    };

    const showBadge = pending > 0 || !online;

    return (
        <div className="min-h-screen bg-slate-950 text-slate-50 flex flex-col">
            <div
                className="h-1"
                style={{
                    background:
                        "repeating-linear-gradient(135deg, #F97316 0 18px, #0f172a 18px 36px)",
                }}
            />

            <header
                className="sticky top-0 z-10 h-14 bg-slate-900/90 backdrop-blur border-b border-slate-800 px-3 flex items-center justify-between"
                data-testid="mobile-header"
            >
                <BrandLockup size={32} />
                <div className="flex items-center gap-2">
                    {showBadge && (
                        <button
                            type="button"
                            onClick={() => setModalOpen(true)}
                            data-testid="pending-badge"
                            className={`inline-flex items-center gap-1.5 h-9 px-2.5 rounded-md border text-xs font-semibold transition ${
                                pending > 0
                                    ? "border-brand-orange/60 bg-brand-orange/10 text-brand-orange hover:bg-brand-orange/20"
                                    : "border-amber-700 bg-amber-900/30 text-amber-300 hover:bg-amber-900/40"
                            }`}
                            title={online ? dict.pending.syncNow : dict.common.offline}
                        >
                            {syncing ? (
                                <Loader2 className="animate-spin" size={13}/>
                            ) : !online ? (
                                <WifiOff size={13}/>
                            ) : (
                                <RefreshCw size={13}/>
                            )}
                            <span className="uppercase tracking-wider">{dict.pending.label}</span>
                            <span data-testid="pending-count" className="font-mono">{pending}</span>
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={handleLogout}
                        data-testid="logout-button"
                        className="inline-flex items-center gap-1 h-9 px-2.5 rounded-md border border-slate-700 hover:border-brand-orange hover:bg-slate-800 text-sm transition"
                    >
                        <LogOut size={15} />
                        <span className="hidden xs:inline">{dict.common.signOut}</span>
                    </button>
                </div>
            </header>

            {flash && (
                <div
                    data-testid="sync-flash"
                    className="bg-emerald-600/15 text-emerald-300 border-b border-emerald-700/50 text-xs px-4 py-1.5 text-center"
                >
                    {flash}
                </div>
            )}

            <main
                className="flex-1 overflow-y-auto pb-24"
                data-testid="mobile-main"
            >
                <Outlet />
            </main>

            {/* Floating manual button (visible on every operator screen) */}
            <button
                type="button"
                onClick={() => navigate("/manual/operador")}
                data-testid="operator-manual-fab"
                aria-label={dict.manuals.items["manual-operador.md"].title}
                title={dict.manuals.items["manual-operador.md"].title}
                className="fixed right-4 z-40 w-14 h-14 rounded-full bg-white text-brand-orange shadow-lg shadow-black/40 border border-slate-200 flex items-center justify-center hover:scale-105 active:scale-95 transition"
                style={{ bottom: "calc(env(safe-area-inset-bottom, 0px) + 80px)" }}
            >
                <BookOpen size={26} strokeWidth={2.2} />
            </button>

            <nav
                className="fixed bottom-0 left-0 right-0 z-50 h-16 bg-slate-900/95 backdrop-blur border-t border-slate-800 flex justify-around"
                data-testid="mobile-tabbar"
                style={{ paddingBottom: "env(safe-area-inset-bottom)" }}
            >
                {TABS.map(({ key, path, icon: Icon, end }) => (
                    <NavLink
                        key={key}
                        to={path}
                        end={end}
                        data-testid={`tab-${key}`}
                        className={({ isActive }) =>
                            [
                                "flex-1 flex flex-col items-center justify-center gap-1 transition relative",
                                isActive
                                    ? "text-brand-orange"
                                    : "text-slate-400 hover:text-slate-100",
                            ].join(" ")
                        }
                    >
                        {({ isActive }) => (
                            <>
                                {isActive && (
                                    <span className="absolute top-0 h-0.5 w-12 bg-brand-orange rounded-b" />
                                )}
                                <Icon size={22} strokeWidth={2.2} />
                                <span className="text-[11px] font-medium uppercase tracking-wider">
                                    {dict.nav[key]}
                                </span>
                            </>
                        )}
                    </NavLink>
                ))}
            </nav>

            <PendingDetailModal
                open={modalOpen}
                onClose={() => setModalOpen(false)}
                online={online}
                onDrain={sync}
                syncing={syncing}
            />
        </div>
    );
}
